import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ClientSupportReport } from '../api/clientReports';
import { clientReportsApi } from '../api/clientReports';
import { adminApi, Client } from '../api/admin';
import { useSelectedClientStore } from '../store/selectedClientStore';
import { notifyError } from '../utils/notify';
import { getClientDisplayName } from '../utils/clientDisplay';
import {
  AdminSupportTicketModal,
  SUPPORT_STATUS_BADGE,
  supportTicketTypeLabel,
  wasSupportTicketReopened,
} from '../components/support/AdminSupportTicketModal';

function SupportTicketsPage() {
  const [searchParams] = useSearchParams();
  const ticketFromUrl = searchParams.get('ticket');
  const { selectedClient } = useSelectedClientStore();
  const [list, setList] = useState<ClientSupportReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [modalTicketId, setModalTicketId] = useState<string | null>(null);
  const [clientsById, setClientsById] = useState<Record<string, Client>>({});

  const load = async () => {
    try {
      setLoading(true);
      const res = await clientReportsApi.listAdmin({
        limit: 300,
        status: statusFilter || undefined,
        clientId: selectedClient?.id || undefined,
      });
      setList(res.data);
    } catch (e: unknown) {
      notifyError(e, 'Failed to load support tickets');
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, selectedClient?.id]);

  useEffect(() => {
    void adminApi.getClients()
      .then((res) => {
        const map: Record<string, Client> = {};
        for (const client of res.data || []) {
          if (!client?.id) continue;
          map[client.id] = client;
        }
        setClientsById(map);
      })
      .catch(() => setClientsById({}));
  }, []);

  useEffect(() => {
    if (!ticketFromUrl || loading) return;
    const hit = list.find((r) => r.id === ticketFromUrl);
    if (hit) {
      setModalTicketId(ticketFromUrl);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        await clientReportsApi.getAdmin(ticketFromUrl);
        if (!cancelled) setModalTicketId(ticketFromUrl);
      } catch {
        /* not found or inaccessible */
      }
    })();
    return () => {
      cancelled = true;
    };
    // Opening from URL/list only; omit modalTicketId so closing the modal stays closed until URL/list changes again.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [ticketFromUrl, loading, list]);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Support Tickets</h1>
        <p className="text-gray-600">Client support requests and issue reports</p>
      </div>

      {selectedClient && (
        <p className="mb-4 rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-indigo-700">
          Filtered by selected client ({getClientDisplayName(selectedClient) || selectedClient.fullName})
        </p>
      )}

      <div className="mb-4 flex items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          <option value="OPEN">OPEN</option>
          <option value="IN_PROGRESS">IN_PROGRESS</option>
          <option value="RESOLVED">RESOLVED</option>
          <option value="CLOSED">CLOSED</option>
        </select>
      </div>

      {loading ? (
        <div className="text-gray-500">Loading tickets…</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Created</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Updated</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Client</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Type</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Status</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Preview</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {list.map((r) => (
                <tr
                  key={r.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => setModalTicketId(r.id)}
                >
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-600">
                    {new Date(r.createdAt).toLocaleString()}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                    {new Date(r.updatedAt).getTime() !== new Date(r.createdAt).getTime()
                      ? new Date(r.updatedAt).toLocaleString()
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700">
                    {getClientDisplayName(clientsById[r.clientId]) || `${String(r.clientId).slice(0, 8)}…`}
                  </td>
                  <td className="px-3 py-2 text-xs">{supportTicketTypeLabel(r.reportType)}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                        SUPPORT_STATUS_BADGE[r.status] ?? 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {r.status}
                      {wasSupportTicketReopened(r) && <span title="Reopened by client">↩</span>}
                    </span>
                  </td>
                  <td className="max-w-xs px-3 py-2 text-xs text-gray-600">
                    <span className="line-clamp-1">{r.description}</span>
                    {r.adminNote && <span className="text-indigo-500"> · replied</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.length === 0 && <p className="p-4 text-sm text-gray-500">No tickets.</p>}
        </div>
      )}

      <AdminSupportTicketModal
        ticketId={modalTicketId}
        open={modalTicketId != null}
        onClose={() => setModalTicketId(null)}
        afterChange={() => void load()}
        getClientLabel={(clientId) => getClientDisplayName(clientsById[clientId])}
      />
    </div>
  );
}

export default SupportTicketsPage;
